// V2 版式 DOM 渲染分发（019_Spec 视觉升级 · 前端侧）
// 与后端 _shared/pptx/v2/ 渲染器一一对应：几何取 v2geo.generated.ts（deno dump 同源），
// 皮肤取 V2SKINS（resolveSkin 的导出快照）。禁止凭感觉写坐标——对照后端渲染器 GEO。

import type { CSSProperties } from "react";
import type { SlideContent } from "../../lib/slides";
import { v2Skin, type V2Skin } from "../../lib/v2geo.generated";
import { px, pt, box, type EditHandlers } from "../SlideCanvas";
import V2Chrome from "./Chrome";
import V2Cover from "./Cover";
import V2Statement from "./Statement";
import V2Divider from "./Divider";
import V2Closing from "./Closing";
import V2KpiHero from "./KpiHero";
import V2BarChart from "./BarChart";
import V2Ledger from "./Ledger";
import V2DuoCompare from "./DuoCompare";
import V2TimelineH from "./TimelineH";
import V2TimelineV from "./TimelineV";
import V2Cards from "./Cards";
import V2Matrix from "./Matrix";
import V2ImageText from "./ImageText";
// KBS 新增 4 版式（032 §4）
import V2PerceptualMap from "./PerceptualMap";
import V2QuadMatrix from "./QuadMatrix";
import V2Pyramid from "./Pyramid";
import V2GridTable from "./GridTable";
// 主题库扩展 v1.1 数据组件 5 版式（043 §4.10）
import V2Waterfall from "./Waterfall";
import V2Gantt from "./Gantt";
import V2Funnel from "./Funnel";
import V2Dumbbell from "./Dumbbell";
import V2Bullet from "./Bullet";

export interface V2SlideProps {
  slide: SlideContent;
  page: number;
  total: number;
  themeId: string;
  editable?: boolean;
  handlers: EditHandlers;
  onImageClick?: (blockIndex: number) => void;
}

/** 各版式组件的公共 props */
export interface V2LayoutProps {
  c: SlideContent;
  skin: V2Skin;
  page: number;
  total: number;
  editable?: boolean;
  handlers: EditHandlers;
  onImageClick?: (blockIndex: number) => void;
}

/** 版式默认底色（与后端 registry 一致；杂志风封面强制 paper，见后端 registry 注） */
export function defaultBg(layout: string, themeId?: string): "paper" | "ink" | "accent" {
  if (layout === "cover") return themeId?.startsWith("mag-") ? "paper" : "accent";
  if (layout === "closing") return "accent";
  if (layout === "divider") return "ink";
  return "paper";
}

/** 杂志风封面 bg 强制修正 + luxe 金句页强制墨黑 + 非法值归一（与后端 registry/lint 同规则） */
export function resolveBg(layout: string, bg: string | undefined, themeId: string): "paper" | "ink" | "accent" {
  if (themeId.startsWith("mag-") && layout === "cover") return "paper";
  // luxe statement 强制墨黑满屏（后端 registry 同款例外；039 §4.1）
  if (themeId.startsWith("luxe-") && layout === "statement") return "ink";
  // 045 #16：term accent→ink 归一（后端 registry 同款；否则预览磷光绿底、导出纯黑）
  if (themeId.startsWith("term-") && bg === "accent") return "ink";
  if (bg === "paper" || bg === "ink" || bg === "accent") return bg;
  return defaultBg(layout, themeId);
}

const LEGACY_MAP: Record<string, string> = {
  cover: "cover", concept: "statement", vision: "closing", data: "kpi-hero",
  insight: "duo-compare", strategy: "cards", channel: "matrix", roadmap: "timeline-h",
  financial: "ledger", risk: "cards",
};

export function resolveLayoutV2(c: SlideContent): string {
  const v = c.layoutV2;
  // 045 #15：白名单校验——非法 layoutV2 回落 LEGACY_MAP（与导出端同归一，防预览/导出不一致）
  if (v && Object.hasOwn(COMPONENTS, v)) return v;
  return LEGACY_MAP[c.layout] ?? "cards";
}

const COMPONENTS: Record<string, (p: V2LayoutProps) => React.ReactElement> = {
  "cover": V2Cover, "divider": V2Divider, "statement": V2Statement, "closing": V2Closing,
  "kpi-hero": V2KpiHero, "bar-chart": V2BarChart, "ledger": V2Ledger,
  "duo-compare": V2DuoCompare, "timeline-h": V2TimelineH, "timeline-v": V2TimelineV,
  "cards": V2Cards, "matrix": V2Matrix, "image-text": V2ImageText,
  "perceptual-map": V2PerceptualMap, "quad-matrix": V2QuadMatrix,
  "pyramid": V2Pyramid, "grid-table": V2GridTable,
  "waterfall": V2Waterfall, "gantt": V2Gantt, "funnel": V2Funnel,
  "dumbbell": V2Dumbbell, "bullet": V2Bullet,
};

export default function V2Slide({ slide, page, total, themeId, editable, handlers, onImageClick }: V2SlideProps) {
  const layout = resolveLayoutV2(slide);
  const skin = v2Skin(themeId, resolveBg(layout, slide.bg, themeId));
  const Comp = COMPONENTS[layout] ?? V2Cards;
  return (
    <div style={{ position: "absolute", inset: 0, background: skin.page }}>
      <Comp c={slide} skin={skin} page={page} total={total} editable={editable} handlers={handlers} onImageClick={onImageClick} />
    </div>
  );
}

export { V2Chrome, px, pt, box };
export type { CSSProperties };
